import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SessionTimeoutBarProps {
  remainingTime: number;
  totalMinutes: number;
  warningMinutes: number;
}

/**
 * Helper function to interpolate between two RGB colors
 */
const interpolateColor = (color1: number[], color2: number[], factor: number): string => {
  const clampedFactor = Math.max(0, Math.min(1, factor));
  const r = Math.round(color1[0] + (color2[0] - color1[0]) * clampedFactor);
  const g = Math.round(color1[1] + (color2[1] - color1[1]) * clampedFactor);
  const b = Math.round(color1[2] + (color2[2] - color1[2]) * clampedFactor);
  return `rgb(${r}, ${g}, ${b})`;
};

// Color definitions [R, G, B]
const GREEN_COLOR = [76, 175, 80];    // #4caf50
const LIGHT_GREEN = [102, 187, 106];  // #66bb6a
const ORANGE_COLOR = [255, 152, 0];   // #ff9800
const RED_COLOR = [244, 67, 54];      // #f44336

const SessionTimeoutBar: React.FC<SessionTimeoutBarProps> = ({
  remainingTime,
  totalMinutes,
  warningMinutes,
}) => {
  const { t: tTopBar } = useTranslation('topbar_config');

  const totalSeconds = totalMinutes * 60;
  const warningSeconds = warningMinutes * 60;
  const halfTimeSeconds = totalSeconds / 2;
  const percentage = (remainingTime / totalSeconds) * 100;
  
  // Format time display
  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  // Calculate color based on remaining time with linear interpolation
  let barColor: string;
  let glowColor: string;

  if (remainingTime <= 10) {
    // Hazard - last 10 seconds (pulsing red)
    barColor = 'linear-gradient(90deg, #d32f2f 0%, #b71c1c 100%)';
    glowColor = 'rgba(183, 28, 28, 0.6)';
  } else if (remainingTime <= warningSeconds) {
    // Warning zone - interpolate from orange to red
    const factor = 1 - ((remainingTime - 10) / (warningSeconds - 10));
    const color = interpolateColor(ORANGE_COLOR, RED_COLOR, factor);
    barColor = `linear-gradient(90deg, ${color} 0%, ${interpolateColor(ORANGE_COLOR, RED_COLOR, Math.min(1, factor + 0.15))} 100%)`;
    glowColor = `rgba(244, 67, 54, ${0.3 + factor * 0.3})`;
  } else if (remainingTime <= halfTimeSeconds) {
    // Between half time and warning - interpolate from green to orange
    const factor = (halfTimeSeconds - remainingTime) / (halfTimeSeconds - warningSeconds);
    const color = interpolateColor(GREEN_COLOR, ORANGE_COLOR, factor);
    const colorEnd = interpolateColor(GREEN_COLOR, ORANGE_COLOR, Math.min(1, factor + 0.15));
    barColor = `linear-gradient(90deg, ${color} 0%, ${colorEnd} 100%)`;
    glowColor = `rgba(255, 152, 0, ${factor * 0.4})`;
  } else {
    // More than half time - interpolate from pure green to slightly yellow-green as approaching half
    const factor = (totalSeconds - remainingTime) / (totalSeconds - halfTimeSeconds);
    const color = interpolateColor(LIGHT_GREEN, GREEN_COLOR, factor);
    barColor = `linear-gradient(90deg, ${color} 0%, rgb(76, 175, 80) 100%)`;
    glowColor = 'rgba(76, 175, 80, 0.3)';
  }

  // Determine text color
  const textColor = remainingTime <= 10 
    ? '#b71c1c' 
    : remainingTime <= warningSeconds 
      ? '#e65100' 
      : '#424242';

  const isHazard = remainingTime <= 10;
  const isWarning = remainingTime <= warningSeconds;

  // Accessible label for screen readers
  const ariaLabel = tTopBar('session_timeout_aria', { 
    defaultValue: `Session timeout: ${minutes} minutes and ${seconds} seconds remaining` 
  });

  return (
    <Tooltip title={tTopBar('session_timeout_tooltip', { defaultValue: 'Session will expire due to inactivity' })}>
      <Box
        tabIndex={0}
        role="timer"
        aria-label={ariaLabel}
        aria-live={isWarning ? 'polite' : 'off'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 0.5,
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
            borderRadius: '4px',
          },
        }}
      >
        {/* Background track - decorative, info provided by parent aria-label */}
        <Box
          aria-hidden="true"
          sx={{
            width: '100px',
            height: '6px',
            borderRadius: '3px',
            backgroundColor: '#e0e0e0',
            overflow: 'hidden',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          {/* Progress fill */}
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              borderRadius: '3px',
              background: barColor,
              transition: 'width 1s linear, background 0.3s linear',
              boxShadow: isWarning ? `0 0 8px ${glowColor}` : 'none',
              animation: isHazard ? 'hazardPulse 0.5s infinite' : 'none',
              '@keyframes hazardPulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.7 },
                '100%': { opacity: 1 },
              },
            }}
          />
        </Box>
        {/* Time text */}
        <Typography
          sx={{
            fontFamily: 'Nunito',
            fontWeight: 600,
            fontSize: '0.7rem',
            color: textColor,
            whiteSpace: 'nowrap',
            minWidth: '35px',
          }}
        >
          {timeDisplay}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default SessionTimeoutBar;
